import {
  memo,
  PropsWithChildren,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import { dialogStack } from '../../_core/dialog-stack';
import { DropdownContext } from '../dropdown.context';

type RootProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const Root = memo(
  ({
    children,
    open: controlledOpen,
    onOpenChange,
  }: PropsWithChildren<RootProps>) => {
    const [internalOpen, setInternalOpen] = useState<boolean>(false);

    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;

    const triggerRef = useRef<HTMLElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const childCloses = useRef<Set<() => void>>(new Set());

    const setOpen = useCallback(
      (value: boolean) => {
        if (!isControlled) {
          setInternalOpen(value);
        }
        onOpenChange?.(value);
      },
      [isControlled, onOpenChange]
    );

    const toggle = useCallback(() => setOpen(!open), [open, setOpen]);
    const close = useCallback(() => setOpen(false), [setOpen]);

    const registerChild = useCallback((childClose: () => void) => {
      childCloses.current.add(childClose);

      return () => childCloses.current.delete(childClose);
    }, []);

    useEffect(() => {
      if (!open) {
        return;
      }

      dialogStack.push(close);

      const onKeyDown = (e: globalThis.KeyboardEvent) => {
        if (e.key !== 'Escape') {
          return;
        }

        if (!dialogStack.isTop(close)) {
          return;
        }

        e.stopPropagation();

        if (childCloses.current.size > 0) {
          childCloses.current.forEach((fn) => fn());
        } else {
          close();
        }
      };

      const onPointerDown = (e: PointerEvent) => {
        const target = e.target as Node;

        if (
          !contentRef.current?.contains(target) &&
          !triggerRef.current?.contains(target)
        ) {
          close();
        }
      };

      document.addEventListener('keydown', onKeyDown);
      document.addEventListener('pointerdown', onPointerDown);

      return () => {
        document.removeEventListener('keydown', onKeyDown);
        document.removeEventListener('pointerdown', onPointerDown);
        dialogStack.pop(close);
      };
    }, [open, close]);

    return (
      <DropdownContext.Provider
        value={{
          open,
          triggerRef,
          contentRef,
          toggle,
          close,
          registerChild,
          closeParent: close,
        }}
      >
        {children}
      </DropdownContext.Provider>
    );
  }
);

Root.displayName = 'Dropdown.Root';
